'use client'

import { useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react'
import type { MethodVisual } from '@/lib/services'
import { Sparkle } from '@/components/site/brand-marks'
import { ScrollProgress } from './scroll-progress'

type MeasureVisual = Extract<MethodVisual, { kind: 'measure' }>

const CHART = { width: 1000, height: 300, top: 44, bottom: 44 }

/** Where each month sits on the chart, as shares of its width and height (0 to 1). */
export function chartPoints(visual: MeasureVisual) {
  const { series, baseline, target } = visual
  const high = Math.max(baseline.value, ...series) + 3
  const low = Math.min(target.value, ...series) - 3
  const inner = (CHART.height - CHART.top - CHART.bottom) / CHART.height
  const y = (value: number) => CHART.top / CHART.height + ((high - value) / (high - low)) * inner
  return {
    points: series.map((value, index) => ({ x: index / (series.length - 1), y: y(value), value })),
    baselineY: y(baseline.value),
    targetY: y(target.value),
  }
}

type Style = CSSProperties & Record<`--${string}`, string | number>
const at = (x: number, y: number): Style => ({ '--x': x.toFixed(4), '--y': y.toFixed(4) })

/**
 * Measure: the metric falling from its dashed baseline to the target, and
 * the sparkle where the return reached 3:1. The line draws left to right as the page
 * scrolls. Pointing at the chart, or focusing it and pressing the arrow
 * keys, reads out any month. Labels are HTML over the lines, so they stay
 * legible at any width.
 */
export function MeasureChart({ visual, id = 'chart' }: { visual: MeasureVisual; id?: string }) {
  const [active, setActive] = useState<number | null>(null)
  const { points, baselineY, targetY } = chartPoints(visual)
  const last = points.length - 1
  const line = points
    .map((point, index) => `${index ? 'L' : 'M'}${point.x * CHART.width} ${(point.y * CHART.height).toFixed(1)}`)
    .join(' ')
  const area = `${line} L${CHART.width} ${CHART.height} L0 ${CHART.height} Z`
  const marker = points[visual.marker.at]
  const summary = `${visual.title}: ${visual.baseline.label} ${visual.baseline.value}, ${visual.target.label} ${visual.target.value}. ${visual.marker.label}.`
  const shown = active === null ? null : points[active]

  const onPointer = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    const share = (event.clientX - box.left) / box.width
    setActive(Math.min(last, Math.max(0, Math.round(share * last))))
  }
  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const step = event.key === 'ArrowRight' ? 1 : -1
    setActive((current) => Math.min(last, Math.max(0, (current ?? (step > 0 ? -1 : last + 1)) + step)))
  }

  return (
    <ScrollProgress className="de-canvas de-canvas--chart">
      <div
        className="de-chart"
        role="img"
        aria-label={summary}
        tabIndex={0}
        onPointerMove={onPointer}
        onPointerLeave={() => setActive(null)}
        onKeyDown={onKey}
        onBlur={() => setActive(null)}
        style={{ '--marker': marker.x.toFixed(4) } as Style}
      >
        <svg className="de-chart-guides" viewBox={`0 0 ${CHART.width} ${CHART.height}`} preserveAspectRatio="none" aria-hidden="true">
          <line className="de-chart-baseline" x1={0} x2={CHART.width} y1={baselineY * CHART.height} y2={baselineY * CHART.height} />
          <line className="de-chart-target" x1={0} x2={CHART.width} y1={targetY * CHART.height} y2={targetY * CHART.height} />
        </svg>
        <svg className="de-chart-lines" viewBox={`0 0 ${CHART.width} ${CHART.height}`} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id={`de-chart-fade-${id}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" style={{ stopColor: 'var(--home-accent)', stopOpacity: 0.18 }} />
              <stop offset="1" style={{ stopColor: 'var(--home-accent)', stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          <path className="de-chart-area" d={area} fill={`url(#de-chart-fade-${id})`} />
          <path className="de-chart-line" d={line} />
        </svg>
        <span className="de-chart-note de-chart-note--baseline" style={at(0, baselineY)}>
          {visual.baseline.label} {visual.baseline.value}
        </span>
        <span className="de-chart-note de-chart-note--target" style={at(1, targetY)}>
          {visual.target.label} {visual.target.value}
        </span>
        <span className="de-chart-marker" style={at(marker.x, marker.y)} aria-hidden="true">
          <span className="de-chart-marker-label">{visual.marker.label}</span>
          <Sparkle className="de-chart-spark" />
        </span>
        <span className="de-chart-axis de-chart-axis--start">{visual.start}</span>
        <span className="de-chart-axis de-chart-axis--end">{visual.end}</span>
        {shown ? (
          <span className="de-chart-cursor" style={at(shown.x, shown.y)} aria-hidden="true">
            <span className="de-chart-readout">
              {visual.monthLabel} {active! + 1} · <strong>{shown.value}</strong>
            </span>
          </span>
        ) : null}
      </div>
    </ScrollProgress>
  )
}
